import styled, { css } from '@xstyled/styled-components';
import React from 'react';
import { t } from 'ttag';
import { PretestChapterRow } from '../state/pretest/pretestOutcome';

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  li {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 12px;
    padding: sm 0;
    border-bottom: 1px solid ${p => p.theme.colors.greyMed};
  }

  li:last-child {
    border-bottom: none;
  }
`;

const Capsule = styled.span<{ done: boolean; next: boolean }>`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--color-grey-md);
  background-color: var(--color-grey-lt);
  font-family: ralewayBold;
  ${p =>
    p.done &&
    css`
      color: var(--color-green-dk);
      background-color: var(--color-green-lt);
      border-color: var(--color-green-md);
    `}
  ${p =>
    p.next &&
    css`
      color: var(--color-on-accent-text);
      background-color: var(--color-accent-dk);
      border-color: var(--color-accent-dk);
    `}
`;

const Title = styled.span`
  min-width: 0;

  small {
    display: block;
    font-size: 13px;
    color: var(--color-grey-dk);
  }
`;

// Raleway only loads its regular weight, so bold text uses ralewayBold.
const Tag = styled.span<{ done: boolean }>`
  font-family: ralewayBold;
  font-size: 14px;
  white-space: nowrap;
  color: ${p => (p.done ? 'var(--color-green-dk)' : 'var(--color-accent-fg)')};
`;

/**
 * Each chapter after grading: tested out (and open for review) or still to take, with the first one to take
 * up next.
 */
export const PretestChapterList = ({ rows }: { rows: PretestChapterRow[] }) => {
  const next = rows.find(row => !row.testedOut);

  return (
    <List aria-label={t`Chapters`}>
      {rows.map(row => {
        const isNext = row === next;
        return (
          <li key={row.id}>
            <Capsule done={row.testedOut} next={isNext} aria-hidden="true">
              {row.number}
            </Capsule>
            <Title>
              {row.title}
              {row.testedOut && <small>{t`You can review it anytime`}</small>}
            </Title>
            {row.testedOut && <Tag done>{t`Tested out`}</Tag>}
            {isNext && <Tag done={false}>{t`Up next`}</Tag>}
          </li>
        );
      })}
    </List>
  );
};
